import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Heart, X, Star, MapPin, Briefcase, GraduationCap, 
  Sparkles, ChevronLeft, ChevronRight, Info, Verified,
  Music, Camera, Plane, Coffee, Dumbbell, Book
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

// Interest icon mapping
const interestIcons = {
  'music': Music,
  'photography': Camera,
  'travel': Plane,
  'coffee': Coffee,
  'fitness': Dumbbell,
  'reading': Book,
};

const getInterestIcon = (interest) => {
  const key = interest.toLowerCase();
  for (const [name, Icon] of Object.entries(interestIcons)) {
    if (key.includes(name)) return Icon;
  }
  return Sparkles;
};

export const Discover = () => {
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);
  
  // Swipe indicators
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);

  useEffect(() => {
    fetchProfiles();
  }, []);

  // Reset photo index when profile changes
  useEffect(() => {
    setCurrentPhotoIndex(0);
    setShowDetails(false);
  }, [currentIndex]);

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/discovery');
      setProfiles(response.data);
    } catch (error) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (direction, superLike = false) => {
    const currentProfile = profiles[currentIndex];
    if (!currentProfile) return;

    try {
      const response = await api.post('/swipe', {
        target_user_id: currentProfile.id,
        action: direction === 'right' ? 'like' : 'pass',
        super_like: superLike
      });

      if (response.data.matched) {
        toast.success("It's a match! 🎉", {
          description: `You and ${currentProfile.name} liked each other!`
        });
      }

      setCurrentIndex(currentIndex + 1);

      if (currentIndex >= profiles.length - 3) {
        fetchProfiles();
      }
    } catch (error) {
      if (error.response?.status >= 500) {
        toast.error('Failed to swipe. Please try again.');
      }
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleDragEnd = (event, info) => {
    if (Math.abs(info.offset.x) > 100) {
      handleSwipe(info.offset.x > 0 ? 'right' : 'left');
    }
  };

  const nextPhoto = (e) => {
    e.stopPropagation();
    const photos = currentProfile?.photos || [];
    if (currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  const currentProfile = profiles[currentIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-pink-50" data-testid="loading-discover">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse"></div>
            <Heart className="w-10 h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-bounce" />
          </div>
          <p className="mt-6 text-lg font-medium text-gray-600">Finding your match...</p>
        </div>
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-purple-50 to-pink-50" data-testid="no-profiles">
        <div className="text-center space-y-6 max-w-sm">
          <div className="w-24 h-24 mx-auto bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
            <Heart className="w-12 h-12 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">No more profiles</h2>
          <p className="text-gray-500">Check back later for new matches!</p>
          <Button 
            onClick={() => navigate('/matches')} 
            className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3"
            data-testid="view-matches-button"
          >
            View Your Matches
          </Button>
        </div>
      </div>
    );
  }

  const photos = currentProfile.photos?.length > 0 
    ? currentProfile.photos 
    : ['https://images.unsplash.com/photo-1658909835269-e76abd3ffb5d?w=600'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-pink-50 p-4 pb-32" data-testid="discover-page">
      <div className="max-w-md mx-auto">
        {/* Card Container */}
        <div className="relative h-[70vh] min-h-[500px] max-h-[650px]" data-testid="swipe-card-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentProfile.id}
              className="absolute inset-0"
              style={{ x, rotate, opacity }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={handleDragEnd}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              data-testid="swipe-card"
            >
              <div className="relative h-full rounded-3xl overflow-hidden shadow-2xl bg-white">
                {/* Photo Section */}
                <div className="relative h-full">
                  {/* Photo */}
                  <img
                    src={photos[currentPhotoIndex]}
                    alt={currentProfile.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Photo Navigation Dots */}
                  {photos.length > 1 && (
                    <div className="absolute top-4 left-0 right-0 flex justify-center gap-1.5 px-4">
                      {photos.map((_, idx) => (
                        <div
                          key={idx}
                          className={`h-1 flex-1 max-w-12 rounded-full transition-all ${
                            idx === currentPhotoIndex 
                              ? 'bg-white' 
                              : 'bg-white/40'
                          }`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Photo Navigation Buttons */}
                  {photos.length > 1 && (
                    <>
                      <button
                        onClick={prevPhoto}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                      >
                        <ChevronLeft className="w-6 h-6 text-white" />
                      </button>
                      <button
                        onClick={nextPhoto}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                      >
                        <ChevronRight className="w-6 h-6 text-white" />
                      </button>
                    </>
                  )}

                  {/* Swipe Indicators */}
                  <motion.div
                    className="absolute top-20 left-6 px-4 py-2 rounded-lg border-4 border-green-500 rotate-[-20deg]"
                    style={{ opacity: likeOpacity }}
                  >
                    <span className="text-2xl font-black text-green-500">LIKE</span>
                  </motion.div>
                  <motion.div
                    className="absolute top-20 right-6 px-4 py-2 rounded-lg border-4 border-red-500 rotate-[20deg]"
                    style={{ opacity: nopeOpacity }}
                  >
                    <span className="text-2xl font-black text-red-500">NOPE</span>
                  </motion.div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80"></div>

                  {/* Profile Info Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    {/* Name & Age */}
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-3xl font-bold" data-testid="profile-name">
                        {currentProfile.name || 'Anonymous'}
                      </h2>
                      <span className="text-2xl font-light">{currentProfile.age || '?'}</span>
                      {currentProfile.verified && (
                        <div className="bg-blue-500 rounded-full p-1">
                          <Verified className="w-4 h-4" />
                        </div>
                      )}
                    </div>

                    {/* Location */}
                    {currentProfile.location?.city && (
                      <div className="flex items-center gap-2 text-white/80 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{currentProfile.location.city}</span>
                        {currentProfile.location.distance && (
                          <span className="text-sm">• {currentProfile.location.distance} km away</span>
                        )}
                      </div>
                    )}

                    {/* Job & Education */}
                    <div className="flex flex-wrap gap-3 mb-3">
                      {currentProfile.job && (
                        <div className="flex items-center gap-1.5 text-white/80">
                          <Briefcase className="w-4 h-4" />
                          <span className="text-sm">{currentProfile.job}</span>
                        </div>
                      )}
                      {currentProfile.education && (
                        <div className="flex items-center gap-1.5 text-white/80">
                          <GraduationCap className="w-4 h-4" />
                          <span className="text-sm">{currentProfile.education}</span>
                        </div>
                      )}
                    </div>

                    {/* Bio Preview */}
                    {currentProfile.bio && (
                      <p className="text-white/90 text-sm line-clamp-2 mb-3" data-testid="profile-bio">
                        {currentProfile.bio}
                      </p>
                    )}

                    {/* Interests */}
                    {currentProfile.interests?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {currentProfile.interests.slice(0, 4).map((interest, idx) => {
                          const InterestIcon = getInterestIcon(interest);
                          return (
                            <span 
                              key={idx} 
                              className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm"
                            >
                              <InterestIcon className="w-3.5 h-3.5" />
                              {interest}
                            </span>
                          );
                        })}
                        {currentProfile.interests.length > 4 && (
                          <span className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm">
                            +{currentProfile.interests.length - 4}
                          </span>
                        )}
                      </div>
                    )}

                    {/* More Info Button */}
                    <button
                      onClick={() => setShowDetails(true)}
                      className="absolute right-4 bottom-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                    >
                      <Info className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center items-center gap-4 mt-6" data-testid="swipe-actions">
          {/* Rewind Button (disabled style for now) */}
          <button
            className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center opacity-50 cursor-not-allowed"
            disabled
            title="Rewind (Premium)"
          >
            <svg className="w-6 h-6 text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>

          {/* Pass Button */}
          <button
            onClick={() => handleSwipe('left')}
            className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 hover:shadow-xl transition-all active:scale-95 border-2 border-red-100"
            data-testid="pass-button"
          >
            <X className="w-8 h-8 text-red-500" strokeWidth={3} />
          </button>
          
          {/* Super Like Button */}
          <button
            onClick={() => handleSwipe('right', true)}
            className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 hover:shadow-xl transition-all active:scale-95 border-2 border-blue-100"
            data-testid="super-like-button"
          >
            <Star className="w-7 h-7 text-blue-500" fill="#3b82f6" />
          </button>
          
          {/* Like Button */}
          <button
            onClick={() => handleSwipe('right')}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg flex items-center justify-center hover:scale-110 hover:shadow-xl transition-all active:scale-95 hover:from-green-500 hover:to-emerald-600"
            data-testid="like-button"
          >
            <Heart className="w-8 h-8 text-white" fill="white" />
          </button>

          {/* Boost Button (disabled style for now) */}
          <button
            className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center opacity-50 cursor-not-allowed"
            disabled
            title="Boost (Premium)"
          >
            <Sparkles className="w-6 h-6 text-purple-500" />
          </button>
        </div>
      </div>

      {/* Profile Details Modal */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center"
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-white w-full max-w-md rounded-t-3xl max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Handle bar */}
              <div className="sticky top-0 bg-white pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
              </div>

              <div className="px-6 pb-8">
                {/* Name Section */}
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {currentProfile.name}, {currentProfile.age}
                  </h2>
                  {currentProfile.verified && (
                    <div className="bg-blue-500 text-white rounded-full p-1">
                      <Verified className="w-4 h-4" />
                    </div>
                  )}
                </div>

                {/* Info Cards */}
                <div className="space-y-3 mb-6">
                  {currentProfile.location?.city && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium text-gray-900">{currentProfile.location.city}</p>
                      </div>
                    </div>
                  )}
                  
                  {currentProfile.job && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Work</p>
                        <p className="font-medium text-gray-900">{currentProfile.job}</p>
                      </div>
                    </div>
                  )}

                  {currentProfile.education && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Education</p>
                        <p className="font-medium text-gray-900">{currentProfile.education}</p>
                      </div>
                    </div>
                  )}

                  {currentProfile.gender && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                        <Heart className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Gender</p>
                        <p className="font-medium text-gray-900 capitalize">{currentProfile.gender}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bio */}
                {currentProfile.bio && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">About</h3>
                    <p className="text-gray-600 leading-relaxed">{currentProfile.bio}</p>
                  </div>
                )}

                {/* All Interests */}
                {currentProfile.interests?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Interests</h3>
                    <div className="flex flex-wrap gap-2">
                      {currentProfile.interests.map((interest, idx) => {
                        const InterestIcon = getInterestIcon(interest);
                        return (
                          <span 
                            key={idx} 
                            className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-sm font-medium"
                          >
                            <InterestIcon className="w-4 h-4" />
                            {interest}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Action Buttons in Modal */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => { setShowDetails(false); handleSwipe('left'); }}
                    className="flex-1 py-3 rounded-full border-2 border-red-200 text-red-500 font-semibold hover:bg-red-50 transition-colors"
                  >
                    Pass
                  </button>
                  <button
                    onClick={() => { setShowDetails(false); handleSwipe('right'); }}
                    className="flex-1 py-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 text-white font-semibold hover:from-green-500 hover:to-emerald-600 transition-colors"
                  >
                    Like
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
